<template>
        <div class="min-h-screen bg-gray-50 font-sans">
                <!-- Header -->
                <header class="bg-white border-b border-gray-200 py-4 px-6">
                        <div class="flex items-center gap-4">
                                <button
                                        type="button"
                                        class="flex items-center justify-center w-10 h-10 bg-transparent border border-gray-200 rounded-lg cursor-pointer text-gray-500"
                                        @click="goBack"
                                >
                                        <svg class="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor" stroke-width="2">
                                                <path stroke-linecap="round" stroke-linejoin="round" d="M15 19l-7-7 7-7" />
                                        </svg>
                                </button>
                                <div>
                                        <h1 class="text-2xl font-semibold text-gray-900 m-0">Edit Offering</h1>
                                </div>
                        </div>
                </header>

                <!-- Loading State -->
                <div v-if="isLoading" class="flex justify-center items-center py-16 px-4">
                        <svg class="w-10 h-10 text-orange-7 animate-spin" fill="none" viewBox="0 0 24 24">
                                <circle class="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" stroke-width="4"></circle>
                                <path class="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                        </svg>
                        <span class="ml-3 text-gray-500 text-sm">Loading offering...</span>
                </div>

                <!-- Main Content -->
                <main v-else class="p-6">
                        <!-- Form Content -->
                        <div class="grid gap-6 grid-cols-1 md:grid-cols-2">
                                <!-- Basic Properties Card -->
                                <div class="bg-white rounded-xl shadow-sm overflow-hidden">
                                        <div class="py-4 px-6 border-b border-gray-200 bg-gray-50">
                                                <h2 class="text-base font-semibold text-gray-900 m-0">Basic Properties</h2>
                                        </div>
                                        <div class="p-6 flex flex-col gap-5">
                                                <!-- Offering ID -->
                                                <div>
                                                        <label class="block text-sm font-medium text-gray-700 mb-1.5">Offering ID</label>
                                                        <input
                                                                type="text"
                                                                :value="formData.id"
                                                                disabled
                                                                class="w-full py-2.5 px-3 border border-gray-300 rounded-lg text-sm bg-gray-50 text-gray-500 box-border"
                                                        >
                                                </div>
                                                <!-- Name -->
                                                <div>
                                                        <label class="block text-sm font-medium text-gray-700 mb-1.5">Name</label>
                                                        <input
                                                                v-model="formData.name"
                                                                type="text"
                                                                placeholder="Enter offering name"
                                                                class="w-full py-2.5 px-3 border border-gray-300 rounded-lg text-sm outline-none transition-all box-border focus:border-orange-7 focus:ring-2 focus:ring-orange-2"
                                                        >
                                                </div>
                                                <!-- External ID -->
                                                <div>
                                                        <label class="block text-sm font-medium text-gray-700 mb-1.5">External ID</label>
                                                        <input
                                                                v-model="formData.externalid"
                                                                type="text"
                                                                placeholder="External production ID"
                                                                class="w-full py-2.5 px-3 border border-gray-300 rounded-lg text-sm outline-none transition-all box-border focus:border-orange-7 focus:ring-2 focus:ring-orange-2"
                                                        >
                                                </div>
                                                <!-- In Stock Toggle -->
                                                <div class="flex items-center justify-between">
                                                        <label class="text-sm font-medium text-gray-700">In Stock</label>
                                                        <button
                                                                type="button"
                                                                class="relative w-11 h-6 rounded-full border-none cursor-pointer transition-colors"
                                                                :class="formData.instock ? 'bg-orange-7' : 'bg-gray-300'"
                                                                @click="formData.instock = formData.instock ? 0 : 1"
                                                        >
                                                                <span
                                                                        class="absolute top-0.5 w-5 h-5 bg-white rounded-full transition-all shadow-sm"
                                                                        :class="formData.instock ? 'left-[1.375rem]' : 'left-0.5'"
                                                                ></span>
                                                        </button>
                                                </div>
                                        </div>
                                </div>

                                <!-- Image Settings Card -->
                                <div class="bg-white rounded-xl shadow-sm overflow-hidden">
                                        <div class="py-4 px-6 border-b border-gray-200 bg-gray-50">
                                                <h2 class="text-base font-semibold text-gray-900 m-0">Images</h2>
                                        </div>
                                        <div class="p-6 flex flex-col gap-5">
                                                <!-- Thumbnail -->
                                                <div>
                                                        <label class="block text-sm font-medium text-gray-700 mb-1.5">Thumbnail</label>
                                                        <div class="flex gap-2 items-center">
                                                                <input
                                                                        v-model="formData.thumbnail"
                                                                        type="text"
                                                                        placeholder="Enter thumbnail URL"
                                                                        class="flex-1 py-2.5 px-3 border border-gray-300 rounded-lg text-sm outline-none box-border focus:border-orange-7 focus:ring-2 focus:ring-orange-2"
                                                                >
                                                                <button
                                                                        v-if="hasFilestackClient"
                                                                        type="button"
                                                                        :disabled="isUploading.thumbnail"
                                                                        class="inline-flex items-center justify-center py-2.5 px-3 bg-gray-100 border border-gray-300 rounded-lg cursor-pointer transition-colors flex-shrink-0 hover:bg-gray-200 disabled:opacity-70 disabled:cursor-not-allowed"
                                                                        title="Upload image"
                                                                        @click="openFilePicker('thumbnail')"
                                                                >
                                                                        <svg v-if="isUploading.thumbnail" class="w-5 h-5 text-gray-500 animate-spin" fill="none" viewBox="0 0 24 24">
                                                                                <circle class="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" stroke-width="4"></circle>
                                                                                <path class="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                                                                        </svg>
                                                                        <svg v-else class="w-5 h-5 text-gray-500" fill="none" viewBox="0 0 24 24" stroke="currentColor" stroke-width="2">
                                                                                <path stroke-linecap="round" stroke-linejoin="round" d="M4 16v1a3 3 0 003 3h10a3 3 0 003-3v-1m-4-8l-4-4m0 0L8 8m4-4v12" />
                                                                        </svg>
                                                                </button>
                                                                <img
                                                                        v-if="formData.thumbnail"
                                                                        :src="formData.thumbnail"
                                                                        alt="Thumbnail"
                                                                        class="w-10 h-10 object-cover rounded-md border border-gray-200 flex-shrink-0"
                                                                >
                                                        </div>
                                                </div>
                                                <!-- Mask -->
                                                <div>
                                                        <label class="block text-sm font-medium text-gray-700 mb-1.5">Mask</label>
                                                        <div class="flex gap-2 items-center">
                                                                <input
                                                                        v-model="formData.mask"
                                                                        type="text"
                                                                        placeholder="Enter mask URL"
                                                                        class="flex-1 py-2.5 px-3 border border-gray-300 rounded-lg text-sm outline-none box-border focus:border-orange-7 focus:ring-2 focus:ring-orange-2"
                                                                >
                                                                <button
                                                                        v-if="hasFilestackClient"
                                                                        type="button"
                                                                        :disabled="isUploading.mask"
                                                                        class="inline-flex items-center justify-center py-2.5 px-3 bg-gray-100 border border-gray-300 rounded-lg cursor-pointer transition-colors flex-shrink-0 hover:bg-gray-200 disabled:opacity-70 disabled:cursor-not-allowed"
                                                                        title="Upload image"
                                                                        @click="openFilePicker('mask')"
                                                                >
                                                                        <svg v-if="isUploading.mask" class="w-5 h-5 text-gray-500 animate-spin" fill="none" viewBox="0 0 24 24">
                                                                                <circle class="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" stroke-width="4"></circle>
                                                                                <path class="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                                                                        </svg>
                                                                        <svg v-else class="w-5 h-5 text-gray-500" fill="none" viewBox="0 0 24 24" stroke="currentColor" stroke-width="2">
                                                                                <path stroke-linecap="round" stroke-linejoin="round" d="M4 16v1a3 3 0 003 3h10a3 3 0 003-3v-1m-4-8l-4-4m0 0L8 8m4-4v12" />
                                                                        </svg>
                                                                </button>
                                                                <img
                                                                        v-if="formData.mask"
                                                                        :src="formData.mask"
                                                                        alt="Mask"
                                                                        class="w-10 h-10 object-cover rounded-md border border-gray-200 flex-shrink-0"
                                                                >
                                                        </div>
                                                </div>
                                                <!-- Overlay -->
                                                <div>
                                                        <label class="block text-sm font-medium text-gray-700 mb-1.5">Overlay</label>
                                                        <div class="flex gap-2 items-center">
                                                                <input
                                                                        v-model="formData.overlay"
                                                                        type="text"
                                                                        placeholder="Enter overlay URL"
                                                                        class="flex-1 py-2.5 px-3 border border-gray-300 rounded-lg text-sm outline-none box-border focus:border-orange-7 focus:ring-2 focus:ring-orange-2"
                                                                >
                                                                <button
                                                                        v-if="hasFilestackClient"
                                                                        type="button"
                                                                        :disabled="isUploading.overlay"
                                                                        class="inline-flex items-center justify-center py-2.5 px-3 bg-gray-100 border border-gray-300 rounded-lg cursor-pointer transition-colors flex-shrink-0 hover:bg-gray-200 disabled:opacity-70 disabled:cursor-not-allowed"
                                                                        title="Upload image"
                                                                        @click="openFilePicker('overlay')"
                                                                >
                                                                        <svg v-if="isUploading.overlay" class="w-5 h-5 text-gray-500 animate-spin" fill="none" viewBox="0 0 24 24">
                                                                                <circle class="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" stroke-width="4"></circle>
                                                                                <path class="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                                                                        </svg>
                                                                        <svg v-else class="w-5 h-5 text-gray-500" fill="none" viewBox="0 0 24 24" stroke="currentColor" stroke-width="2">
                                                                                <path stroke-linecap="round" stroke-linejoin="round" d="M4 16v1a3 3 0 003 3h10a3 3 0 003-3v-1m-4-8l-4-4m0 0L8 8m4-4v12" />
                                                                        </svg>
                                                                </button>
                                                                <img
                                                                        v-if="formData.overlay"
                                                                        :src="formData.overlay"
                                                                        alt="Overlay"
                                                                        class="w-10 h-10 object-cover rounded-md border border-gray-200 flex-shrink-0"
                                                                >
                                                        </div>
                                                </div>
                                        </div>
                                </div>

                                <!-- Enhancement Settings Card -->
                                <div class="bg-white rounded-xl shadow-sm overflow-hidden">
                                        <div class="py-4 px-6 border-b border-gray-200 bg-gray-50">
                                                <h2 class="text-base font-semibold text-gray-900 m-0">Enhancement Settings</h2>
                                        </div>
                                        <div class="p-6 flex flex-col gap-4">
                                                <!-- Apply Upscaling -->
                                                <div class="flex items-center justify-between">
                                                        <div>
                                                                <label class="text-sm font-medium text-gray-700 block">Apply Upscaling</label>
                                                                <span class="text-xs text-gray-500">Enhance image resolution</span>
                                                        </div>
                                                        <button
                                                                type="button"
                                                                class="relative w-11 h-6 rounded-full border-none cursor-pointer transition-colors"
                                                                :class="formData.applyUpscaling ? 'bg-orange-7' : 'bg-gray-300'"
                                                                @click="formData.applyUpscaling = formData.applyUpscaling ? 0 : 1"
                                                        >
                                                                <span
                                                                        class="absolute top-0.5 w-5 h-5 bg-white rounded-full transition-all shadow-sm"
                                                                        :class="formData.applyUpscaling ? 'left-[1.375rem]' : 'left-0.5'"
                                                                ></span>
                                                        </button>
                                                </div>
                                                <!-- Apply Enhancement -->
                                                <div class="flex items-center justify-between">
                                                        <div>
                                                                <label class="text-sm font-medium text-gray-700 block">Apply Enhancement</label>
                                                                <span class="text-xs text-gray-500">Apply AI image enhancement</span>
                                                        </div>
                                                        <button
                                                                type="button"
                                                                class="relative w-11 h-6 rounded-full border-none cursor-pointer transition-colors"
                                                                :class="formData.applyEnhancement ? 'bg-orange-7' : 'bg-gray-300'"
                                                                @click="formData.applyEnhancement = formData.applyEnhancement ? 0 : 1"
                                                        >
                                                                <span
                                                                        class="absolute top-0.5 w-5 h-5 bg-white rounded-full transition-all shadow-sm"
                                                                        :class="formData.applyEnhancement ? 'left-[1.375rem]' : 'left-0.5'"
                                                                ></span>
                                                        </button>
                                                </div>
                                        </div>
                                </div>

                                <!-- Reference IDs Card -->
                                <div class="bg-white rounded-xl shadow-sm overflow-hidden">
                                        <div class="py-4 px-6 border-b border-gray-200 bg-gray-50">
                                                <h2 class="text-base font-semibold text-gray-900 m-0">References</h2>
                                        </div>
                                        <div class="p-6 flex flex-col gap-5">
                                                <!-- PDP ID -->
                                                <div>
                                                        <label class="block text-sm font-medium text-gray-700 mb-1.5">PDP ID</label>
                                                        <input
                                                                v-model.number="formData.pdpid"
                                                                type="number"
                                                                placeholder="Product Detail Page ID"
                                                                class="w-full py-2.5 px-3 border border-gray-300 rounded-lg text-sm outline-none box-border focus:border-orange-7 focus:ring-2 focus:ring-orange-2"
                                                        >
                                                </div>
                                                <!-- Model 2D ID -->
                                                <div>
                                                        <label class="block text-sm font-medium text-gray-700 mb-1.5">Model 2D ID</label>
                                                        <input
                                                                v-model.number="formData.model2did"
                                                                type="number"
                                                                placeholder="2D Model ID"
                                                                class="w-full py-2.5 px-3 border border-gray-300 rounded-lg text-sm outline-none box-border focus:border-orange-7 focus:ring-2 focus:ring-orange-2"
                                                        >
                                                </div>
                                                <!-- Model 3D ID -->
                                                <div>
                                                        <label class="block text-sm font-medium text-gray-700 mb-1.5">Model 3D ID</label>
                                                        <input
                                                                v-model.number="formData.model3did"
                                                                type="number"
                                                                placeholder="3D Model ID"
                                                                class="w-full py-2.5 px-3 border border-gray-300 rounded-lg text-sm outline-none box-border focus:border-orange-7 focus:ring-2 focus:ring-orange-2"
                                                        >
                                                </div>
                                                <!-- Theme ID -->
                                                <div>
                                                        <label class="block text-sm font-medium text-gray-700 mb-1.5">Theme ID</label>
                                                        <input
                                                                v-model.number="formData.themeid"
                                                                type="number"
                                                                placeholder="Theme ID"
                                                                class="w-full py-2.5 px-3 border border-gray-300 rounded-lg text-sm outline-none box-border focus:border-orange-7 focus:ring-2 focus:ring-orange-2"
                                                        >
                                                </div>
                                                <!-- Flex Group ID -->
                                                <div>
                                                        <label class="block text-sm font-medium text-gray-700 mb-1.5">Flex Group ID</label>
                                                        <input
                                                                v-model.number="formData.flexgroupid"
                                                                type="number"
                                                                placeholder="Flex Group ID"
                                                                class="w-full py-2.5 px-3 border border-gray-300 rounded-lg text-sm outline-none box-border focus:border-orange-7 focus:ring-2 focus:ring-orange-2"
                                                        >
                                                </div>
                                        </div>
                                </div>
                        </div>

                        <!-- Save Button at bottom of form -->
                        <div class="mt-6 flex justify-end">
                                <button
                                        type="button"
                                        :disabled="isSaving"
                                        class="inline-flex items-center gap-2 bg-orange-7 text-white py-2.5 px-6 rounded-lg text-sm font-medium border-none cursor-pointer transition-colors hover:bg-orange-6 disabled:opacity-70 disabled:cursor-not-allowed"
                                        @click="saveChanges"
                                >
                                        <svg v-if="isSaving" class="w-4 h-4 animate-spin" fill="none" viewBox="0 0 24 24">
                                                <circle class="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" stroke-width="4"></circle>
                                                <path class="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                                        </svg>
                                        <svg v-else class="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" stroke-width="2">
                                                <path stroke-linecap="round" stroke-linejoin="round" d="M5 13l4 4L19 7" />
                                        </svg>
                                        {{ isSaving ? 'Saving...' : 'Save Changes' }}
                                </button>
                        </div>
                </main>

                <!-- Toast Notification -->
                <div v-if="toastMessage" class="fixed bottom-6 right-6 z-50">
                        <div
                                class="flex items-center gap-3 py-4 px-5 rounded-lg shadow-lg"
                                :class="toastVariant === 'success' ? 'bg-emerald-600' : 'bg-red-600'"
                        >
                                <svg v-if="toastVariant === 'success'" class="w-5 h-5 text-white" fill="none" viewBox="0 0 24 24" stroke="currentColor" stroke-width="2">
                                        <path stroke-linecap="round" stroke-linejoin="round" d="M5 13l4 4L19 7" />
                                </svg>
                                <svg v-else class="w-5 h-5 text-white" fill="none" viewBox="0 0 24 24" stroke="currentColor" stroke-width="2">
                                        <path stroke-linecap="round" stroke-linejoin="round" d="M12 8v4m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                                </svg>
                                <span class="text-sm font-medium text-white">{{ toastMessage }}</span>
                                <button
                                        type="button"
                                        class="ml-2 text-white opacity-70 bg-transparent border-none cursor-pointer hover:opacity-100"
                                        @click="hideToast"
                                >
                                        <svg class="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" stroke-width="2">
                                                <path stroke-linecap="round" stroke-linejoin="round" d="M6 18L18 6M6 6l12 12" />
                                        </svg>
                                </button>
                        </div>
                </div>
        </div>
</template>

<script>
import { defineComponent, ref, computed, watch, getCurrentInstance } from 'vue';
import { useRoute, useRouter } from 'vue-router';
import { useAuth0 } from '@auth0/auth0-vue';
import OfferingService from 'services/OfferingService';

export default defineComponent({
        name: 'EditOfferingPage',
        setup() {
                const route = useRoute();
                const router = useRouter();
                const auth0 = useAuth0();
                const instance = getCurrentInstance();
                const filestackClient = instance?.appContext.config.globalProperties.$client;

                const isLoading = ref(true);
                const isSaving = ref(false);
                const isUploading = ref({ thumbnail: false, mask: false, overlay: false });
                const formData = ref({
                        id: null,
                        name: '',
                        externalid: '',
                        instock: 1,
                        thumbnail: '',
                        mask: '',
                        overlay: '',
                        applyUpscaling: 0,
                        applyEnhancement: 0,
                        pdpid: null,
                        model2did: null,
                        model3did: null,
                        themeid: null,
                        flexgroupid: null,
                });

                const toastMessage = ref('');
                const toastVariant = ref('success');
                let toastTimeout;

                const offeringId = computed(() => route.params.id);
                const loggedIn = computed(() => auth0.isAuthenticated.value);
                const hasFilestackClient = computed(() => !!filestackClient);

                const showToast = (message, variant = 'success') => {
                        toastMessage.value = message;
                        toastVariant.value = variant;
                        if (toastTimeout) clearTimeout(toastTimeout);
                        toastTimeout = setTimeout(() => {
                                toastMessage.value = '';
                        }, 4000);
                };

                const hideToast = () => {
                        toastMessage.value = '';
                        if (toastTimeout) clearTimeout(toastTimeout);
                };

                const loadData = async () => {
                        if (!loggedIn.value) return;

                        isLoading.value = true;
                        try {
                                const data = await OfferingService.get(offeringId.value);
                                formData.value = { ...data };
                        } catch (error) {
                                showToast(error.message || 'Failed to load offering', 'error');
                        } finally {
                                isLoading.value = false;
                        }
                };

                const saveChanges = async () => {
                        if (!loggedIn.value || isSaving.value) return;

                        isSaving.value = true;
                        try {
                                await OfferingService.update(offeringId.value, { ...formData.value });
                                showToast('Offering updated successfully', 'success');
                                setTimeout(() => {
                                        router.push('/offerings');
                                }, 1000);
                        } catch (error) {
                                showToast(error.message || 'Failed to update offering', 'error');
                        } finally {
                                isSaving.value = false;
                        }
                };

                const goBack = () => {
                        router.push('/offerings');
                };

                const openFilePicker = (field) => {
                        if (!filestackClient) {
                                showToast('File upload is not available', 'error');
                                return;
                        }
                        isUploading.value[field] = true;
                        const options = {
                                accept: 'image/*',
                                maxFiles: 1,
                                onUploadDone: (files) => {
                                        if (files.filesUploaded && files.filesUploaded.length > 0) {
                                                formData.value[field] = files.filesUploaded[0].url;
                                        }
                                        isUploading.value[field] = false;
                                },
                                onClose: () => {
                                        isUploading.value[field] = false;
                                },
                        };
                        filestackClient.picker(options).open();
                };

                watch(loggedIn, (isLoggedIn) => {
                        if (isLoggedIn) {
                                loadData();
                        }
                }, { immediate: true });

                return {
                        isLoading,
                        isSaving,
                        isUploading,
                        formData,
                        toastMessage,
                        toastVariant,
                        hasFilestackClient,
                        saveChanges,
                        goBack,
                        openFilePicker,
                        hideToast,
                };
        },
});
</script>
